// Client Activity tab. Chronological feed of touchpoints (email/call/
// meeting/note interactions logged against this client by any staff
// member). Data from GET /clients/:id/touchpoints, which already exists
// (apps/api/src/routes/touchpoints.ts).
//
// Reps log new interactions via the inline "+ Log activity" composer,
// which POSTs to /touchpoints (backend: requireAnyRole(...ALL_STAFF_ROLES)
// — any staff role, so the affordance is gated to staff roles here too).
// On success the feed query is invalidated so the entry appears instantly.
// Composer pattern mirrors ClientContactsTab's inline "+ Contact" form.
//
// Rep name isn't joined on the route yet — feed shows type + direction
// + subject + body preview + when. Can extend the service to LEFT JOIN
// users when reps ask for "who logged this" labels.
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useOutletContext } from "react-router-dom";
import { Mail, Phone, Calendar, MessageSquare, FileText } from "lucide-react";
import { ApiError, api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { StatusPill } from "@/components/ui/StatusPill";
import { useAuth } from "@/auth/AuthProvider";
import { timeAgo } from "@/lib/utils";
import type { ClientDetailContext } from "@/pages/ClientDetailPage";
import { MentionTextarea, NoteText } from "@/components/notes/MentionTextarea";
import { EditDeleteNote } from "@/components/notes/EditDeleteNote";

type TouchpointType = "email" | "call" | "meeting" | "note" | string;
type TouchpointDirection = "inbound" | "outbound" | null;

interface TouchpointRow {
  id: string;
  clientId: string;
  projectId: string | null;
  contactId: string | null;
  userId: string;
  touchpointType: TouchpointType;
  direction: TouchpointDirection;
  subject: string | null;
  body: string | null;
  occurredAt: string;
  createdAt: string | null;
}

const TYPE_ICON: Record<string, typeof Mail> = {
  email: Mail,
  call: Phone,
  meeting: Calendar,
  note: MessageSquare,
};

const TYPE_LABEL: Record<string, string> = {
  email: "Email",
  call: "Call",
  meeting: "Meeting",
  note: "Note",
  sms: "SMS",
  linkedin: "LinkedIn",
  other: "Other",
};

// Mirror the backend enums (apps/api/src/db/schema/enums.ts:601-615).
// These are the only values POST /touchpoints accepts.
const TYPE_OPTIONS = [
  "note",
  "call",
  "email",
  "meeting",
  "sms",
  "linkedin",
  "other",
] as const;
const DIRECTION_OPTIONS = ["outbound", "inbound", "internal"] as const;

// Any internal staff role may log a touchpoint (backend gate is
// requireAnyRole(...ALL_STAFF_ROLES)). Keep this in sync with
// apps/api/src/middleware/auth.ts:106-112 so the button never shows to a
// role the endpoint would 403.
const STAFF_ROLES = [
  "admin",
  "client_rep",
  "order_rep",
  "production_rep",
  "finance",
] as const;

const SELECT_CLASS =
  "flex h-10 w-full rounded-md border border-brown-100 bg-white px-3 text-sm text-brown-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-peach-400 focus-visible:ring-offset-2 focus-visible:ring-offset-cream-50 disabled:cursor-not-allowed disabled:opacity-60";

/** Today as YYYY-MM-DD in the viewer's local timezone (for <input type=date>). */
function todayLocal(): string {
  return new Date().toLocaleDateString("en-CA");
}

export function ClientActivityTab() {
  const { clientId } = useOutletContext<ClientDetailContext>();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const canLog =
    user !== null && STAFF_ROLES.some((r) => user.roles.includes(r));

  const { data, isLoading, isError } = useQuery({
    queryKey: ["clients", clientId, "touchpoints"],
    queryFn: () =>
      api<{ touchpoints: TouchpointRow[] }>(
        `/clients/${clientId}/touchpoints?limit=100`,
      ),
    enabled: clientId.length > 0,
  });

  const events = data?.touchpoints ?? [];

  const [isLogging, setIsLogging] = useState(false);
  const [type, setType] = useState<(typeof TYPE_OPTIONS)[number]>("note");
  const [direction, setDirection] =
    useState<(typeof DIRECTION_OPTIONS)[number]>("outbound");
  const [occurredOn, setOccurredOn] = useState(todayLocal);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  // "@" tags (Stephanie 2026-10-21): they get the bell + an email.
  const [mentions, setMentions] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const resetForm = (): void => {
    setType("note");
    setDirection("outbound");
    setOccurredOn(todayLocal());
    setSubject("");
    setBody("");
    setMentions([]);
    setError(null);
  };

  const createMutation = useMutation({
    mutationFn: async (payload: {
      client_id: string;
      touchpoint_type: string;
      direction: string;
      subject?: string;
      body: string;
      occurred_at: string;
      mention_user_ids?: string[];
    }) =>
      api<{ touchpoint: { id: string } }>("/touchpoints", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["clients", clientId, "touchpoints"],
      });
      setIsLogging(false);
      resetForm();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(`Couldn't log activity (${err.status})`);
      } else {
        setError("Couldn't log activity — see console");
      }
    },
  });

  return (
    <section>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">
            Activity{" "}
            {events.length > 0 && (
              <span className="font-normal text-muted">({events.length})</span>
            )}
          </h2>
          <p className="mt-0.5 text-xs text-muted">Calls, emails, meetings and notes, newest first.</p>
        </div>
        {canLog && !isLogging && (
          <Button
            variant="outline"
            onClick={() => {
              setIsLogging(true);
              setError(null);
            }}
          >
            Log activity
          </Button>
        )}
      </div>

      {isLogging && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (body.trim().length === 0) {
              setError("Add a note describing the interaction.");
              return;
            }
            if (occurredOn.trim().length === 0) {
              // Guard before the Date() construction below — an empty date
              // string makes `new Date("T12:00:00")` throw a synchronous
              // RangeError inside this handler, which would silently abort
              // submit (no banner, no request). Reject it explicitly.
              setError("Pick a date.");
              return;
            }
            setError(null);
            createMutation.mutate({
              client_id: clientId,
              touchpoint_type: type,
              direction,
              subject:
                subject.trim().length > 0 ? subject.trim() : undefined,
              body: body.trim(),
              mention_user_ids: mentions,
              // Anchor the picked calendar day at local noon so the ISO
              // conversion never shifts it across the date line.
              occurred_at: new Date(`${occurredOn}T12:00:00`).toISOString(),
            });
          }}
          className="mb-4 rounded-md border border-rule bg-white p-5 sm:p-[22px]"
        >
          <h3 className="mb-3 text-lg font-semibold tracking-[-0.4px] text-ink">Log activity</h3>
          <div className="grid gap-3 sm:grid-cols-3">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-muted">
                Type
              </span>
              <select
                className={SELECT_CLASS}
                value={type}
                onChange={(e) =>
                  setType(e.target.value as (typeof TYPE_OPTIONS)[number])
                }
              >
                {TYPE_OPTIONS.map((t) => (
                  <option key={t} value={t}>
                    {TYPE_LABEL[t] ?? t}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-muted">
                Direction
              </span>
              <select
                className={SELECT_CLASS}
                value={direction}
                onChange={(e) =>
                  setDirection(
                    e.target.value as (typeof DIRECTION_OPTIONS)[number],
                  )
                }
              >
                {DIRECTION_OPTIONS.map((d) => (
                  <option key={d} value={d}>
                    {d.charAt(0).toUpperCase() + d.slice(1)}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-muted">
                Date
              </span>
              <Input
                type="date"
                required
                value={occurredOn}
                max={todayLocal()}
                onChange={(e) => setOccurredOn(e.target.value)}
              />
            </label>
          </div>
          <label className="mt-3 block">
            <span className="mb-1 block text-xs font-medium text-muted">
              Subject <span className="normal-case text-brown-300">(optional)</span>
            </span>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={300}
              placeholder="Short summary"
            />
          </label>
          <label className="mt-3 block">
            <span className="mb-1 block text-xs font-medium text-muted">
              Note
            </span>
            <MentionTextarea
              autoFocus
              value={body}
              onChange={(t, ids) => { setBody(t); setMentions(ids); }}
              rows={4}
              placeholder="What happened on this call / email / meeting? Type @ to tag someone"
              className="flex w-full rounded-md border border-brown-100 bg-white px-3 py-2 text-sm text-brown-500 placeholder:text-[#8a8a85] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-peach-400 focus-visible:ring-offset-2 focus-visible:ring-offset-cream-50 disabled:cursor-not-allowed disabled:opacity-60"
            />
          </label>
          {error && (
            <div className="mt-3 rounded-md bg-risk-bg px-3 py-2 text-sm text-risk" role="alert">
              {error}
            </div>
          )}
          <div className="mt-4 flex gap-2">
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? "Logging…" : "Log activity"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setIsLogging(false);
                resetForm();
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}

      {isLoading ? (
        <div className="h-24 animate-pulse rounded-md bg-white" aria-label="Loading activity" />
      ) : isError ? (
        <div className="rounded-md border border-rust-200 bg-rust-50 px-4 py-3 text-sm text-rust-500">
          Couldn't load activity. Try refreshing.
        </div>
      ) : events.length === 0 ? (
        !isLogging && (
          <div className="rounded-md border border-rule bg-white px-5 py-12 text-center">
            <p className="font-medium text-ink">No activity logged yet.</p>
            <p className="mt-1 text-sm text-muted">
              Emails, calls, meetings and notes logged against this account land here.
            </p>
          </div>
        )
      ) : (
        <ol className="overflow-hidden rounded-md border border-rule bg-white">
          {events.map((ev) => {
            const Icon = TYPE_ICON[ev.touchpointType] ?? FileText;
            const typeLabel =
              TYPE_LABEL[ev.touchpointType] ?? ev.touchpointType;
            // Internal notes are labelled as such (handoff W07) — never
            // confused with something the client saw.
            const isInternal =
              ev.direction === ("internal" as TouchpointDirection) ||
              (ev.touchpointType === "note" && ev.direction !== "inbound" && ev.direction !== "outbound");
            return (
              <li
                key={ev.id}
                className="flex gap-3 border-b border-[#e7e7e4] px-4 py-3.5 last:border-0 sm:px-5"
              >
                <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-canvas text-ink">
                  <Icon className="h-3.5 w-3.5" aria-hidden />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="text-sm font-medium text-ink">
                      {typeLabel}
                    </span>
                    {isInternal ? (
                      <StatusPill tone="warn">Internal note</StatusPill>
                    ) : ev.direction ? (
                      <span className="text-xs text-muted">
                        {ev.direction === "inbound" ? "From the client" : "To the client"}
                      </span>
                    ) : null}
                    <span className="text-xs text-muted">
                      · {timeAgo(ev.occurredAt)}
                    </span>
                  </div>
                  {/* Edit / Delete (Stephanie 2026-10-30: anything a rep
                      enters can be edited and deleted). */}
                  <EditDeleteNote
                    noun={typeLabel.toLowerCase()}
                    fields={[
                      { key: "subject", label: "Subject", value: ev.subject ?? "", required: false },
                      { key: "body", label: "Note", value: ev.body ?? "", kind: "textarea" },
                    ]}
                    onSave={async (v) => {
                      await api(`/touchpoints/${ev.id}`, {
                        method: "PATCH",
                        body: JSON.stringify({ subject: (v.subject ?? "").trim() || null, body: (v.body ?? "").trim() }),
                      });
                      await queryClient.invalidateQueries({ queryKey: ["clients", clientId, "touchpoints"] });
                    }}
                    onDelete={async () => {
                      await api(`/touchpoints/${ev.id}`, { method: "DELETE" });
                      await queryClient.invalidateQueries({ queryKey: ["clients", clientId, "touchpoints"] });
                    }}
                  >
                    {ev.subject && (
                      <div className="mt-1 text-sm font-medium text-ink">
                        {ev.subject}
                      </div>
                    )}
                    {ev.body && (
                      <div className="mt-1 whitespace-pre-wrap text-sm text-muted">
                        <NoteText text={ev.body} />
                      </div>
                    )}
                  </EditDeleteNote>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
